import { useState } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiPost } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

interface Reward {
  id: string;
  name: string;
  description: string | null;
  type: string;
  pointsCost: number;
  value: Record<string, unknown>;
  availableQuantity: number | null;
  redeemedCount: number;
  isActive: boolean;
  imageUrl: string | null;
}

interface LoyaltyRewardsProps {
  title?: string;
  columns?: '2' | '3';
  showPointsBalance?: boolean;
}

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#127873;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to view rewards
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to browse and redeem exclusive rewards with your points.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

const TYPE_ICONS: Record<string, string> = {
  discount: '\uD83C\uDFF7\uFE0F',
  shipping: '\uD83D\uDE9A',
  free_item: '\uD83C\uDF81',
  custom: '\u2B50',
};

function RewardCard({
  reward,
  balance,
  onRedeem,
  redeeming,
}: {
  reward: Reward;
  balance: number;
  onRedeem: (id: string) => void;
  redeeming: string | null;
}) {
  const canAfford = balance >= reward.pointsCost;
  const soldOut =
    reward.availableQuantity !== null &&
    reward.redeemedCount >= reward.availableQuantity;
  const icon = TYPE_ICONS[reward.type] ?? '\u2B50';
  const valueLabel = formatValue(reward);

  return (
    <div className="loyalty-card overflow-hidden flex flex-col hover:shadow-lg transition-shadow">
      {/* Image / placeholder */}
      <div className="relative h-32 bg-[var(--color-surface)] flex items-center justify-center">
        {reward.imageUrl ? (
          <img
            src={reward.imageUrl}
            alt={reward.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-4xl opacity-30">{icon}</span>
        )}
        {/* Type badge */}
        <span className="absolute top-2 left-2 loyalty-tag-skew text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-black/50 backdrop-blur-sm text-white">
          {reward.type.replace(/_/g, ' ')}
        </span>
        {/* Value badge */}
        {valueLabel && (
          <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[var(--color-primary)] text-white">
            {valueLabel}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="loyalty-heading text-sm text-[var(--color-text)] mb-1 line-clamp-1">
          {reward.name}
        </h3>
        {reward.description && (
          <p className="text-xs text-[var(--color-text-muted)] mb-3 line-clamp-2">
            {reward.description}
          </p>
        )}

        {/* Stock */}
        {reward.availableQuantity !== null && (
          <div className="text-[10px] text-[var(--color-text-muted)] mb-2">
            {soldOut
              ? 'Sold out'
              : `${reward.availableQuantity - reward.redeemedCount} remaining`}
          </div>
        )}

        {/* Action area */}
        <div className="mt-auto flex items-center justify-between">
          <span className="text-sm font-bold text-[var(--color-primary)]">
            {reward.pointsCost.toLocaleString()} pts
          </span>
          <button
            onClick={() => onRedeem(reward.id)}
            disabled={!canAfford || soldOut || redeeming === reward.id}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
              !canAfford || soldOut
                ? 'bg-[var(--color-surface-border)] text-[var(--color-text-muted)] cursor-not-allowed'
                : 'bg-[var(--color-primary)] text-white hover:opacity-90 active:scale-[0.98]'
            }`}
          >
            {redeeming === reward.id
              ? 'Redeeming...'
              : soldOut
              ? 'Sold Out'
              : !canAfford
              ? 'Not enough pts'
              : 'Redeem'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatValue(reward: Reward): string | null {
  const v = reward.value as { unit?: string; amount?: number };
  if (!v?.unit || !v?.amount) return null;
  if (v.unit === 'percent') return `${v.amount}% OFF`;
  if (v.unit === 'fixed') return `$${v.amount} OFF`;
  if (v.unit === 'free') return 'FREE';
  return null;
}

export function LoyaltyRewardsBlock({ props }: { props: LoyaltyRewardsProps }) {
  const { isAuthenticated } = useAuth();
  const { data, loading: loyaltyLoading, refetch } = useLoyalty();
  const title = props.title ?? 'Rewards Catalog';
  const columns = props.columns ?? '3';
  const showPointsBalance = props.showPointsBalance ?? true;

  const [redeeming, setRedeeming] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  if (loyaltyLoading) {
    return (
      <div className="loyalty-card p-8 animate-pulse">
        <div className="h-6 w-48 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-56 bg-[var(--color-surface-border)] rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;

  // Get rewards from the full loyalty data (rewards are inside each account)
  // We call /customer/loyalty to get full data with rewards included
  const rewards: Reward[] = (data as unknown as { rewards?: Reward[] }).rewards ?? [];
  const balance = data.profile.pointsBalance;

  const gridCols = columns === '2' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  async function handleRedeem(rewardId: string) {
    if (!data) return;
    setRedeeming(rewardId);
    setToast(null);
    try {
      await apiPost('/customer/loyalty/redeem', {
        programId: data.programId,
        rewardId,
      });
      setToast({ type: 'success', message: 'Reward redeemed successfully!' });
      refetch(); // Refresh loyalty data (balance, etc.)
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? 'Failed to redeem reward';
      setToast({ type: 'error', message: msg });
    } finally {
      setRedeeming(null);
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="loyalty-heading text-xl text-[var(--color-text)]">{title}</h2>
        {showPointsBalance && (
          <div className="loyalty-card px-4 py-2 flex items-center gap-2">
            <span className="text-sm text-[var(--color-text-muted)]">Your Balance:</span>
            <span className="text-lg font-bold text-[var(--color-primary)]">
              {balance.toLocaleString()} pts
            </span>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Rewards grid */}
      {rewards.length > 0 ? (
        <div className={`grid ${gridCols} gap-4`}>
          {rewards
            .filter((r) => r.isActive)
            .map((reward) => (
              <RewardCard
                key={reward.id}
                reward={reward}
                balance={balance}
                onRedeem={handleRedeem}
                redeeming={redeeming}
              />
            ))}
        </div>
      ) : (
        <div className="loyalty-card p-8 text-center text-[var(--color-text-muted)] text-sm">
          No rewards available right now. Check back soon!
        </div>
      )}
    </div>
  );
}
